import React, { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import RequireAuth from "./components/RequireAuth";
import RequireIntegratorAuth from "./components/integrator/RequireIntegratorAuth";
import { AuthProvider } from "./contexts/AuthContext";
import { IntegratorAuthProvider } from "./contexts/IntegratorAuthContext";
import { FEATURES } from "./config/features";

// ── Páginas de autenticação ──────────────────────────────────────────────────
const SignInPage                      = lazy(() => import("./routes/SignInPage"));
const SignUpPage                      = lazy(() => import("./routes/SignUpPage"));
const ForgotPasswordPage              = lazy(() => import("./routes/ForgotPasswordPage"));
const ResetPasswordPage               = lazy(() => import("./routes/ResetPasswordPage"));
const ErrorPage                       = lazy(() => import("./routes/ErrorPage"));
const UnauthorizedPage                = lazy(() => import("./routes/UnauthorizedPage"));
const IntegratorLoginPage             = lazy(() => import("./routes/integrator/IntegratorLoginPage"));
const IntegratorSetPasswordPage       = lazy(() => import("./routes/integrator/IntegratorSetPasswordPage"));

// ── Páginas de usuário ───────────────────────────────────────────────────────
const HomePage                        = lazy(() => import("./routes/HomePage"));
const FinancesManagementPage          = lazy(() => import("./routes/FinancesManagementPage"));
const ActivitiesPage                  = lazy(() => import("./routes/ActivitiesPage"));
const ManagementPage                  = lazy(() => import("./routes/ManagementPage"));
const StagesPage                      = lazy(() => import("./routes/StagesPage"));
const ComissionsPage                  = lazy(() => import("./routes/Comissions"));
const NewMeetingPage                  = lazy(() => import("./routes/meeting/page"));
const WhatsAppPage                    = lazy(() => import("./routes/WhatsAppPage"));
const EquipmentCatalogPage            = lazy(() => import("./routes/EquipmentCatalogPage"));
const IntegratorLayout                = lazy(() => import("./components/integrator/IntegratorLayout"));
const IntegratorDashboardPage         = lazy(() => import("./routes/integrator/IntegratorDashboardPage"));
const IntegratorProjectsPage          = lazy(() => import("./routes/integrator/IntegratorProjectsPage"));
const IntegratorProjectDetailPage     = lazy(() => import("./routes/integrator/IntegratorProjectDetailPage"));
const IntegratorProjectRequestPage    = lazy(() => import("./routes/integrator/IntegratorProjectRequestPage"));
const IntegratorProjectRequestDetailPage = lazy(() => import("./routes/integrator/IntegratorProjectRequestDetailPage"));
const IntegratorFinancialPage         = lazy(() => import("./routes/integrator/IntegratorFinancialPage"));

// ── Gestão individual ────────────────────────────────────────────────────────
const ProjectsPage                    = lazy(() => import("./routes/manage/ProjectsPage"));
const ProjectEditPage                 = lazy(() => import("./routes/manage/ProjectEditPage"));
const ProjectRequestsPage             = lazy(() => import("./routes/manage/ProjectRequestsPage"));
const ProjectRequestDetailPage        = lazy(() => import("./routes/manage/ProjectRequestDetailPage"));
const IntegratorsPage                 = lazy(() => import("./routes/manage/IntegratorsPage"));
const IntegratorEditPage              = lazy(() => import("./routes/manage/IntegratorEditPage"));
const PowerCompaniesPage              = lazy(() => import("./routes/manage/PowerCompaniesPage"));
const PowerCompanyEditPage            = lazy(() => import("./routes/manage/PowerCompanyEditPage"));
const EngineersPage                   = lazy(() => import("./routes/manage/EngineersPage"));
const EngineerEditPage                = lazy(() => import("./routes/manage/EngineerEditPage"));
const AttorneysPage                   = lazy(() => import("./routes/manage/AttorneysPage"));

// ── Páginas de admin ─────────────────────────────────────────────────────────
const AdminPanelPage                  = lazy(() => import("./routes/admin/AdminPanelPage"));
const TimeManagementPage              = lazy(() => import("./routes/admin/TimeManagementPage"));
const TemplateChecklistManagementPage = lazy(() => import("./routes/admin/TemplateChecklistManagementPage"));
const ActivitiesAnalysisPage          = lazy(() => import("./routes/ActivitiesAnalysisPage"));

const ROLES = {
  User: 5050,
  Admin: 2019,
};

function PageLoader() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <IntegratorAuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/signIn" element={<SignInPage />} />
              <Route path="/login" element={<SignInPage />} />
              <Route path="/signUp" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/integrador/login" element={<IntegratorLoginPage />} />
              <Route path="/integrador/definir-senha" element={<IntegratorSetPasswordPage />} />

              <Route element={<RequireIntegratorAuth />}>
                <Route path="/integrador" element={<IntegratorLayout />}>
                  <Route index element={<Navigate to="/integrador/dashboard" replace />} />
                  <Route path="dashboard" element={<IntegratorDashboardPage />} />
                  <Route path="projetos" element={<IntegratorProjectsPage />} />
                  <Route path="projetos/:projectId" element={<IntegratorProjectDetailPage />} />
                  <Route path="novo-projeto" element={<IntegratorProjectRequestPage />} />
                  <Route path="solicitacoes" element={<IntegratorProjectsPage />} />
                  <Route path="solicitacoes/:requestId" element={<IntegratorProjectRequestDetailPage />} />
                  <Route path="financeiro" element={<IntegratorFinancialPage />} />
                </Route>
              </Route>

              <Route path="*" element={<ErrorPage />} />

              <Route element={<RequireAuth allowedRoles={[ROLES.User, ROLES.Admin]} />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/finances" element={<FinancesManagementPage />} />
                <Route path="/activities" element={<ActivitiesPage />} />

                {/* Rotas de gestão individual (URLs limpas) */}
                <Route path="/projects/:id" element={<ProjectEditPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/project-requests" element={<ProjectRequestsPage />} />
                <Route path="/project-requests/:requestId" element={<ProjectRequestDetailPage />} />
                <Route path="/integrators/:id" element={<IntegratorEditPage />} />
                <Route path="/integrators" element={<IntegratorsPage />} />
                <Route path="/power-companies/:id" element={<PowerCompanyEditPage />} />
                <Route path="/power-companies" element={<PowerCompaniesPage />} />
                <Route path="/engineers/:id" element={<EngineerEditPage />} />
                <Route path="/engineers" element={<EngineersPage />} />
                <Route path="/attorneys" element={<AttorneysPage />} />
                <Route path="/equipment" element={<EquipmentCatalogPage />} />

                {/* Rota de gestão antiga (compatibilidade) */}
                <Route path="/manage" element={<ManagementPage />} />

                <Route path="/stages" element={<StagesPage />} />
                <Route path="/comissions" element={FEATURES.commissions ? <ComissionsPage /> : <Navigate to="/home" replace />} />
                <Route path="/meeting" element={<NewMeetingPage />} />
                <Route path="/whatsapp" element={<WhatsAppPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
              </Route>

              <Route element={<RequireAuth allowedRoles={[ROLES.Admin]} />}>
                <Route path="/admin-panel" element={<AdminPanelPage />} />
                <Route path="/time-management" element={<TimeManagementPage />} />
                <Route path="/template-checklist" element={<TemplateChecklistManagementPage />} />
                <Route path="/activities-analysis" element={<ActivitiesAnalysisPage />} />
              </Route>
            </Routes>
          </Suspense>
        </IntegratorAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
